import { useEffect, useState } from "react";

import * as Api from "@modules/api";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@ui/card";
import TranslatedText from "@components/items/display/TranslatedText";
import { SVG } from "@components/items/common/SVG";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import {
  getInterests,
  setInterests,
  setIsContentPrepareLoading,
  setOnboardingCompleted,
  useAppSelector,
} from "@lib/stores";
import { useGetInterests, useSetInterests } from "@lib/stores/content/hooks";
import { useNavigateToHome } from "@utils/hooks/useNavigateToHome";

export interface Interest {
  id: string;
  icon: string;
  shortTitle: string;
}

export const Onboarding = () => {
  const navigateToHome = useNavigateToHome();

  const [selectedInterests, setSelectedInterests] = useState<
    Api.Server.InterestOutput[]
  >([]);

  const { data: interests } = useGetInterests();
  const { status: setInterestsStatus } = useSetInterests();

  const isContentPrepareLoading = useAppSelector(
    ({ auth }) => auth.isContentPrepareLoading
  );
  const isOnboardingCompleted = useAppSelector(
    ({ auth }) => auth.user.onboardingCompleted
  );

  const handleSelect = (interest: Api.Server.InterestOutput) => {
    const isSelected = selectedInterests.find(
      (item) => item.id === interest.id
    );
    if (!isSelected && selectedInterests.length < 5) {
      setSelectedInterests((prev) => [...prev, interest]);
    } else if (isSelected) {
      setSelectedInterests((prev) =>
        prev.filter((item) => item.id !== interest.id)
      );
    }
  };

  useEffect(() => {
    getInterests();
  }, []);

  useEffect(() => {
    if (isOnboardingCompleted && !isContentPrepareLoading) {
      navigateToHome();
    }
  }, [isOnboardingCompleted, navigateToHome, isContentPrepareLoading]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isContentPrepareLoading) {
      timeout = setTimeout(() => {
        setIsContentPrepareLoading(false);
      }, 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isContentPrepareLoading, navigateToHome]);

  return !isContentPrepareLoading ? (
    <Card className="flex w-[1000px] flex-col items-center justify-center rounded-xl border-none text-white dark:bg-dark_background">
      <CardHeader className="space-y-3">
        <CardTitle>
          <TranslatedText
            text={`onboarding.titles.interests-title-1`}
            className="text-center text-[24px] font-bold leading-[1.1]"
          />
        </CardTitle>
        <TranslatedText
          text={`onboarding.titles.interests-title-2`}
          className="text-center text-[16px] font-normal leading-[1.1]"
        />
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-center gap-x-4 gap-y-8">
        {interests?.map((i) => (
          <div
            key={i.id}
            className={`flex items-center justify-center px-[16px] py-[10px] ${
              selectedInterests.find((item) => item.id === i.id)
                ? "bg-primary text-[#EEEEF2]"
                : "bg-[#EEEEF2] text-primary"
            } h-[48px] gap-3 rounded-lg ${
              selectedInterests?.length < 5 &&
              "hover:bg-primary hover:text-[#EEEEF2]"
            } group cursor-pointer`}
            onClick={() => {
              handleSelect(i);
            }}
          >
            <SVG
              type="svg"
              src={i.iconUrl || ""}
              className={`${
                selectedInterests.find((item) => item.id === i.id)
                  ? "text-[#EEEEF2]"
                  : "text-primary"
              } ${
                selectedInterests?.length < 5 && "group-hover:text-[#EEEEF2]"
              }`}
              preProcessor={(code: string) =>
                code
                  .replace(/(?<=fill=)"(?!none")[^"]+/g, `"currentColor"`)
                  .replace(/(?<=stroke=)"(?!none")[^"]+/g, `"currentColor"`)
                  .replace(
                    /fill:\s*(?!none)\s*([^;]*?)(?=\s*;)/g,
                    `fill: currentColor;`
                  )
                  .replace(
                    /stroke:\s*(?!none)\s*([^;]*?)(?=\s*;)/g,
                    `stroke: currentColor;`
                  )
              }
            />
            <div className="text-[18px] font-bold">{i?.shortTitle}</div>
          </div>
        ))}
      </CardContent>
      <CardFooter>
        <ShadcnButton
          disabled={
            !selectedInterests.length || setInterestsStatus === "loading"
          }
          onClick={() => {
            setInterests(
              {
                contentEntityIds: selectedInterests.map((i) => i.id || ""),
              },
              async () => {
                setIsContentPrepareLoading(true);

                setOnboardingCompleted();
              }
            );
          }}
          isLoading={setInterestsStatus === "loading"}
        >
          <TranslatedText
            text={`onboarding.buttons.${"start now"}`}
            className="text-[20px] font-medium text-white"
          />
        </ShadcnButton>
      </CardFooter>
    </Card>
  ) : (
    <div className="fixed left-0 top-0 flex h-full w-full flex-col items-center justify-center bg-gray-900 bg-opacity-60">
      <div className="flex h-[35%] flex-col items-center">
        <div className="text-[32px] font-bold text-white">
          <TranslatedText text={`onboarding.titles.content-prepare-loading`} />
        </div>
        <svg
          className="mt-[40px] h-32 w-32 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          width="128"
          height="128"
          viewBox="0 0 128 128"
          fill="none"
        >
          <path
            d="M128 64C128 99.3462 99.3462 128 64 128C28.6538 128 0 99.3462 0 64C0 28.6538 28.6538 0 64 0C99.3462 0 128 28.6538 128 64ZM15.6214 64C15.6214 90.7188 37.2812 112.379 64 112.379C90.7188 112.379 112.379 90.7188 112.379 64C112.379 37.2812 90.7188 15.6214 64 15.6214C37.2812 15.6214 15.6214 37.2812 15.6214 64Z"
            fill="#454440"
          />
          <path
            d="M53.2493 8.89373C52.4186 4.63589 55.2002 0.459584 59.5277 0.156444C66.6041 -0.339256 73.7324 0.343742 80.618 2.19512C90.0394 4.72832 98.7569 9.3819 106.104 15.8002C113.452 22.2185 119.235 30.2316 123.011 39.2271C125.771 45.8014 127.406 52.7733 127.865 59.8521C128.147 64.1811 124.382 67.4986 120.051 67.2477C115.72 66.9968 112.479 63.2635 112.025 58.9492C111.533 54.2695 110.358 49.6726 108.526 45.308C105.677 38.5205 101.313 32.4743 95.7693 27.6315C90.2253 22.7887 83.6476 19.2774 76.5389 17.366C71.9676 16.1369 67.2545 15.5902 62.551 15.7314C58.2149 15.8616 54.08 13.1516 53.2493 8.89373Z"
            fill="#8E908F"
          />
        </svg>
      </div>
    </div>
  );
};
